/**
 * Bundle 164 A2 - Broken Names tile for /admin/incidents.
 *
 * Live count from the cached `_card_broken_names` count.
 * Clicking the tile deep-links to /admin/users/broken-names.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { UserX } from 'lucide-react';
import api from '@/utils/api';

const BrokenNamesTile = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: body } = await api.get(
          '/admin/incidents/tiles/broken-names');
        if (alive) setData(body);
      } catch (e) {
        if (alive) {
          setError(e?.response?.data?.detail
            || 'Failed to load tile.');
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);
  const count = data?.count ?? 0;
  return (
    <Link
      to="/admin/users/broken-names"
      className="block group"
      data-testid="broken-names-tile-link"
    >
      <Card
        className={
          'border shadow-soft transition-all hover:shadow-md '
          + (count > 0
            ? 'border-amber-200 hover:border-amber-300'
            : 'border-neutral-200 hover:border-primary-300')
        }
        data-testid="broken-names-tile"
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <UserX className="h-4 w-4 text-primary-800" />
                <div className="text-[10px] uppercase tracking-widest text-secondary-700">
                  Broken names
                </div>
              </div>
              <div className="text-sm text-primary-900 font-semibold mb-2">
                {loading ? 'Loading.' : error
                  ? 'Could not load'
                  : count > 0
                    ? 'Users with blank first / last name'
                    : 'All names look populated'}
              </div>
              <div className="flex items-baseline gap-3">
                <span
                  className={`text-2xl font-semibold ${
                    count > 0 ? 'text-amber-700' : 'text-neutral-500'
                  }`}
                  data-testid="broken-names-tile-count"
                >
                  {count}
                </span>
                {data?.source ? (
                  <span
                    className="text-[10px] uppercase tracking-widest text-neutral-500"
                    data-testid="broken-names-tile-source"
                  >
                    {data.source}
                    {data.source === 'cache'
                      ? ` (${data.cache_age_s}s)` : ''}
                  </span>
                ) : null}
              </div>
              {error ? (
                <div className="text-xs text-red-700 mt-2">{error}</div>
              ) : null}
            </div>
            <div className="text-xs text-secondary-700 group-hover:text-primary-900 whitespace-nowrap">
              Open list →
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default BrokenNamesTile;
